/** A note box that shows bold as bold.
 *
 * The client's words about what this replaces: "this simple B and H is not
 * so good and showing ** is so confusing". They were right — the markers
 * are a storage format and a learner should never have met them.
 *
 * So the box is a contentEditable and the formatting is real. What is
 * STORED is unchanged (see richText.tsx): the same plain text with the same
 * markers, converted at the two edges by noteHtml.ts. That keeps every note
 * written before today readable, needs no migration, and means there is
 * still no user-authored HTML anywhere in the system to sanitise.
 *
 * Two things about editing a contentEditable in React that this file exists
 * to get right:
 *
 *   * It cannot be a controlled input. Writing `innerHTML` on every
 *     keystroke destroys the caret, so the DOM is seeded once and then left
 *     alone — `lastEmitted` tracks what we last reported so an outside
 *     change (switching to a different note) is still picked up.
 *   * The browser's own bold command is the only sane way to apply a mark
 *     across an arbitrary selection; splitting and merging text nodes by
 *     hand is where hand-rolled editors go wrong. What each browser
 *     PRODUCES differs, so noteHtml normalises on the way out rather than
 *     trying to constrain it on the way in.
 */
import { useEffect, useRef, useState } from "react";
import { useT } from "../../i18n";
import { tokens } from "../../theme";
import { noteFromElement, toHtml } from "./noteHtml";

export interface RichTextEditorProps {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
  /** Minimum height in rems, so compose and edit can differ. */
  minRem?: number;
}

type Command = "bold" | "italic" | "hiliteColor";

interface ToolButton {
  key: "bold" | "italic" | "highlight";
  command: Command;
  /** Passed to execCommand for the colour commands. */
  argument?: string;
  /** queryCommandState uses a different name for highlight. */
  state: string;
}

/** execCommand takes a colour value, not a CSS variable, so the highlight
 *  is read from the TS view of the palette — the same place charts and the
 *  canvas orb read theirs. A literal here would go stale at the next
 *  rebrand, silently. */
export const HIGHLIGHT = tokens.accentSoft;

const BUTTONS: ToolButton[] = [
  { key: "bold", command: "bold", state: "bold" },
  { key: "italic", command: "italic", state: "italic" },
  { key: "highlight", command: "hiliteColor", argument: HIGHLIGHT, state: "hiliteColor" },
];

export function RichTextEditor({
  value, onChange, label, placeholder, minRem = 6,
}: RichTextEditorProps) {
  const t = useT();
  const boxRef = useRef<HTMLDivElement | null>(null);
  /** The last note WE reported upward. An incoming value that differs from
   *  it came from somewhere else and has to be written into the DOM. */
  const lastEmitted = useRef<string | null>(null);
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    if (value === lastEmitted.current) return; // our own keystroke coming back
    box.innerHTML = toHtml(value);
    lastEmitted.current = value;
    setEmpty(!value);
  }, [value]);

  /** Read the box, tell the caller. */
  const emit = () => {
    const box = boxRef.current;
    if (!box) return;
    const next = noteFromElement(box);
    lastEmitted.current = next;
    setEmpty(!box.textContent?.trim());
    onChange(next);
  };

  /** Light the buttons that apply where the caret is. */
  const refreshActive = () => {
    const state: Record<string, boolean> = {};
    for (const b of BUTTONS) {
      try {
        state[b.key] =
          b.command === "hiliteColor"
            ? isHighlighted()
            : document.queryCommandState(b.command);
      } catch {
        // Not supported here; the button still works, it just cannot show
        // whether it is on.
        state[b.key] = false;
      }
    }
    setActive(state);
  };

  /** queryCommandState("hiliteColor") is not reliable, so ask the DOM where
   *  the caret is instead. */
  const isHighlighted = (): boolean => {
    const sel = window.getSelection?.();
    let node: Node | null = sel?.anchorNode ?? null;
    while (node && node !== boxRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName.toLowerCase() === "mark") return true;
        if (el.style?.backgroundColor) return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  const run = (button: ToolButton) => {
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    try {
      document.execCommand(button.command, false, button.argument);
    } catch {
      // Nothing we can do; leave the note as it is rather than mangling it.
      return;
    }
    emit();
    refreshActive();
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {BUTTONS.map((button) => (
          <button
            key={button.key}
            type="button"
            aria-label={t(`notebook.${button.key}` as never)}
            aria-pressed={Boolean(active[button.key])}
            // The box must not lose the selection when the button is
            // pressed, or there is nothing to format.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(button)}
            className={`h-9 w-9 rounded-control border text-sm ${
              active[button.key]
                ? "border-brand bg-brand-soft text-brand-dark"
                : "border-line text-ink hover:border-brand"
            }`}
          >
            {button.key === "bold" && <span className="font-extrabold">B</span>}
            {button.key === "italic" && <span className="italic font-serif">I</span>}
            {button.key === "highlight" && (
              <span className="rounded-sm px-1" style={{ background: HIGHLIGHT }}>
                H
              </span>
            )}
          </button>
        ))}
        <span className="ml-1 text-xs text-muted">{t("notebook.formatHint")}</span>
      </div>

      <div className="relative">
        {empty && placeholder && (
          // A contentEditable has no placeholder of its own.
          <p
            aria-hidden
            className="pointer-events-none absolute left-3 top-2 text-sm text-muted"
          >
            {placeholder}
          </p>
        )}
        <div
          ref={boxRef}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          // Paste as PLAIN TEXT. Letting a browser drop styled markup in
          // would put things in the box that we cannot store, so the note
          // would silently change the moment it was saved.
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
            emit();
          }}
          style={{ minHeight: `${minRem}rem` }}
          className="w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-brand [&_mark]:rounded-sm [&_mark]:px-0.5"
        />
      </div>
    </div>
  );
}
